// The player's own maps, kept in this browser's localStorage under one key: { name -> { map, updatedAt } }.
// What the in-game editor saves to in production and what the bot-match map picker offers. Every map is
// validated on the way in and again on the way out (the stored JSON is the player's to tamper with).
import { MapValidationError, validateMap, type MapData, type MapIssue, type MapIssueCode } from '../core/map/schema.js';

export const MAPS_KEY = 'bestiarum-maps';
/** A map file is ~10 KB; an import far past this is not a map. */
export const MAX_MAP_FILE_BYTES = 512 * 1024;
export const MAX_MAP_NAME = 40;

export type Result<T> = { ok: true; value: T } | { ok: false; issues: MapIssue[] };
export type LocalMapEntry = { name: string; updatedAt: string };
type Stored = Record<string, { map: unknown; updatedAt: string }>;

const fail = (code: MapIssueCode, params?: MapIssue['params']): { ok: false; issues: MapIssue[] } => ({ ok: false, issues: [{ path: '', code, ...(params ? { params } : {}) }] });
const failWith = (issue: MapIssue): { ok: false; issues: MapIssue[] } => ({ ok: false, issues: [issue] });

/** Parsed-JSON -> MapData, or the validator's issues (codes, one per problem; the UI words them). */
export function checkedMap(raw: unknown): Result<MapData> {
  try {
    return { ok: true, value: validateMap(raw) };
  } catch (err) {
    if (err instanceof MapValidationError) return { ok: false, issues: err.issues };
    console.error(err);
    return fail('mapUnreadable');
  }
}

/** The refusal for a file of `bytes` too large to be a map, or null. */
export const oversizeMapFile = (bytes: number) => bytes > MAX_MAP_FILE_BYTES ? fail('fileTooLarge', { kb: MAX_MAP_FILE_BYTES / 1024 }) : null;

/** A map file's text (an import) -> a valid map, or why not. */
export function parseMapFile(text: string): Result<MapData> {
  const big = oversizeMapFile(new TextEncoder().encode(text).byteLength);
  if (big) return big;
  let raw: unknown;
  try { raw = JSON.parse(text); } catch { return fail('notJson'); }
  return checkedMap(raw);
}

/** null when `name` is usable as a stored map's name, else why not. */
export function checkMapName(name: string): MapIssue | null {
  if (!name.trim()) return { path: '', code: 'nameEmpty' };
  if (name !== name.trim()) return { path: '', code: 'nameSpaces' };
  if (name.length > MAX_MAP_NAME) return { path: '', code: 'nameTooLong', params: { max: MAX_MAP_NAME } };
  return null;
}

const isQuota = (err: unknown) => err instanceof DOMException && (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED' || err.code === 22);

function defaultStorage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

export type LocalMaps = ReturnType<typeof localMaps>;

export function localMaps(storage: Storage | null = defaultStorage()) {
  function read(): Stored {
    try {
      const v = JSON.parse(storage?.getItem(MAPS_KEY) ?? '{}') as unknown;
      if (typeof v !== 'object' || v === null || Array.isArray(v)) return {};
      // an entry that is not even { map, ... } (hand-edited storage) is not listed at all
      return Object.fromEntries(Object.entries(v).filter(([, e]) => typeof e === 'object' && e !== null && !Array.isArray(e) && Object.prototype.hasOwnProperty.call(e, 'map'))) as Stored;
    } catch { return {}; }
  }
  function write(all: Stored): Result<void> {
    if (!storage) return fail('storageUnavailable');
    try {
      storage.setItem(MAPS_KEY, JSON.stringify(all));
      return { ok: true, value: undefined };
    } catch (err) {
      if (isQuota(err)) return fail('storageFull');
      return fail('storageWriteFailed', { error: String(err instanceof Error ? err.message : err) });
    }
  }
  const has = (all: Stored, name: string) => Object.prototype.hasOwnProperty.call(all, name);

  return {
    available: () => !!storage,
    /** Newest first. */
    list(): LocalMapEntry[] {
      return Object.entries(read())
        .map(([name, e]) => ({ name, updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : '' }))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.name.localeCompare(b.name));
    },
    load(name: string): Result<MapData> {
      const all = read();
      if (!has(all, name)) return fail('notFound', { name });
      return checkedMap(all[name]!.map);
    },
    save(name: string, map: MapData, now = new Date()): Result<void> {
      const bad = checkMapName(name);
      if (bad) return failWith(bad);
      const valid = checkedMap(map);
      if (!valid.ok) return valid;
      return write({ ...read(), [name]: { map: valid.value, updatedAt: now.toISOString() } });
    },
    rename(from: string, to: string): Result<void> {
      const bad = checkMapName(to);
      if (bad) return failWith(bad);
      const all = read();
      if (!has(all, from)) return fail('notFound', { name: from });
      if (from === to) return { ok: true, value: undefined };
      if (has(all, to)) return fail('nameTaken', { name: to });
      const { [from]: entry, ...rest } = all;
      return write({ ...rest, [to]: entry! });
    },
    remove(name: string): Result<void> {
      const all = read();
      if (!has(all, name)) return fail('notFound', { name });
      const { [name]: _, ...rest } = all;
      return write(rest);
    },
  };
}
